import { type JSX, useState } from 'react';

import { routes } from '../routes';
import { useContentOverride } from '../content/ContentProvider';
import { languageForLocale, regionForLocale, useI18n } from '../i18n';
import { useHeaderVisibility } from './motion';
import { JTButton, PageContainer } from './primitives';
import { DestinationLink } from './primitives';
import { LanguageModal } from './LanguageModal';
import { MegaMenu } from './MegaMenu';

/**
 * The global header.
 *
 * The mockup's structure and style, unchanged: wordmark left, controls right,
 * hiding on scroll down and returning on scroll up.
 *
 * ## What it deliberately does not hold
 *
 * Search and a delivery-location picker used to live here. Both are gone —
 * from the header only. The capability is untouched; the controls belong in
 * the discovery flow, where a search box can show results and a postcode can
 * be checked against a real service area. In a header, above a platform that
 * has neither service yet, they were two boxes that accepted input and
 * discarded it.
 *
 * ## Alignment
 *
 * The contents sit in `PageContainer`, the same container the sections below
 * use, so the wordmark starts on the same vertical line as the headings under
 * it. Previously the header was inset by the gutter alone, which at 1440px put
 * it 81px to the left of everything else.
 */

export function JTHeader(): JSX.Element {
  const { hidden, raised } = useHeaderVisibility();
  const { locale, t } = useI18n();
  // The dashboard's Header blocks. An unedited block leaves the translated
  // label in place, so a second locale is not overwritten with English.
  const edited = useContentOverride();
  const [languageOpen, setLanguageOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const region = regionForLocale(locale);
  const language = languageForLocale(locale);

  return (
    <>
      {/*
        Two elements, the same shape as the footer: the bar is full-bleed so
        the sticky blur spans the viewport, and the container inside it holds
        the content on the grid. Putting both on one element makes the gutter
        and the max-width compound, which pushed the wordmark 64px past every
        heading below it.
      */}
      <header
        className={`jt-header ${hidden ? 'jt-header--hidden' : ''} ${raised ? 'jt-header--raised' : ''}`}
      >
        <PageContainer className="jt-header__inner">
          <a href={routes.home.path} className="jt-header__brand" aria-label={t('nav.home')}>
            Just Treated
          </a>

          <nav className="jt-header__nav" aria-label="Main">
            <button
              type="button"
              className="jt-header__region"
              onClick={() => setLanguageOpen(true)}
              aria-haspopup="dialog"
              // The visible text is a flag and a language code, which tells a
              // screen reader nothing about what the button does.
              aria-label={`${t('nav.changeRegion')} — ${region.name}, ${language.englishName}`}
            >
              <span aria-hidden="true">{region.flag}</span>
              <span aria-hidden="true">{language.locale.slice(0, 2).toUpperCase()}</span>
            </button>

            <DestinationLink destination={routes.support} className="jt-header__link">
              {edited('header.support') ?? t('nav.support')}
            </DestinationLink>

            <JTButton destination={routes.signUp} variant="ink" analyticsKey="header_sign_up">
              {edited('header.signup') ?? t('nav.signUp')}
            </JTButton>

            <button
              type="button"
              className="jt-header__burger"
              onClick={() => setMenuOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
              aria-label={t('nav.openMenu')}
            >
              <span className="jt-header__burger-bar" aria-hidden="true" />
              <span className="jt-header__burger-bar" aria-hidden="true" />
              <span className="jt-header__burger-bar" aria-hidden="true" />
            </button>
          </nav>
        </PageContainer>
      </header>

      {/*
        Rendered outside the header because the header is `position: sticky`
        with a transform on it — a fixed-position child of a transformed
        ancestor is positioned against that ancestor, not the viewport, so an
        overlay inside would be clipped to a 78px-tall strip.
      */}
      <LanguageModal open={languageOpen} onClose={() => setLanguageOpen(false)} />
      <MegaMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
